import { memo, useMemo, useRef } from 'react';
import merge from 'lodash.merge';
import { Bar as BarChartJS } from 'react-chartjs-2';
import { useChartPrint } from '@hooks';
import { verticalBarGradient } from '@helpers/chartGradients';

const isBarDataset = dataset => dataset.type === undefined || dataset.type === 'bar';

const supportsGradient = (data, optionsProps) => {
    const barDatasets = (data.datasets || []).filter(isBarDataset);
    const isStacked = optionsProps?.scales?.x?.stacked !== false && optionsProps?.scales?.y?.stacked !== false;
    return barDatasets.length === 1 || !isStacked;
};

const withGradientBars = (data, optionsProps) => {
    if (!supportsGradient(data, optionsProps)) return data;
    return {
        ...data,
        datasets: (data.datasets || []).map(dataset =>
            isBarDataset(dataset) && typeof dataset.backgroundColor === 'string'
                ? {
                      ...dataset,
                      hoverBackgroundColor: dataset.hoverBackgroundColor ?? dataset.backgroundColor,
                      backgroundColor: verticalBarGradient(dataset.backgroundColor),
                  }
                : dataset,
        ),
    };
};

const createChartOptions = (legend = '', labels, optionsOverride) => {
    const defaultOptions = {
        elements: {
            bar: {
                borderWidth: 0,
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            chartAreaBorder: {
                borderColor: 'rgba(0, 0, 0, 0.3)',
                borderWidth: 1,
            },
            legend: {
                display: true,
                position: 'bottom',
                align: 'start',
                reverse: true,
                onClick: () => null,
                labels: {
                    usePointStyle: true,
                    pointStyle: 'rectRounded',
                    font: {
                        family: 'Rubik',
                        size: 10,
                        weight: 400,
                    },
                },
            },
            tooltip: {
                displayColors: false,
                enabled: true,
                backgroundColor: 'rgba(0, 0, 0, 1)',
                callbacks: {
                    title: () => null,
                    label: function (context) {
                        return `${context.dataset.label} - ${context.formattedValue}`;
                    },
                },
            },
        },
        scales: {
            x: {
                stacked: true,
                border: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.3)',
                },
                grid: {
                    display: false,
                    tickLength: 0,
                    drawTicks: false,
                },
                ticks: {
                    autoSkip: false,
                    padding: 8,
                    font: {
                        family: 'Rubik',
                        size: 10,
                        weight: 400,
                    },
                    callback: function (_, index) {
                        return labels[index].toUpperCase();
                    },
                },
            },
            y: {
                border: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.3)',
                    dash: [5, 5],
                },
                grid: {
                    tickLength: 0,
                    drawTicks: false,
                },
                ticks: {
                    font: {
                        family: 'Rubik',
                        size: 10,
                    },
                    color: 'rgba(0, 0, 0, 0.6)',
                    padding: 10,
                },
                stacked: true,
                title: {
                    display: true,
                    position: 'left',
                    align: 'center',
                    font: {
                        family: 'Rubik',
                        size: 10,
                        weight: 500,
                    },
                    color: 'rgba(0, 0, 0, 0.6)',
                    text: legend.toUpperCase(),
                    padding: {
                        bottom: 14,
                    },
                },
            },
        },
    };
    return merge({}, defaultOptions, optionsOverride);
};

const chartAreaBorder = {
    id: 'chartAreaBorder',
    beforeDraw(chart) {
        const { ctx } = chart;
        ctx.save();
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.lineWidth = chart.config.options.scales.x.grid.lineWidth || 3;
        ctx.beginPath();
        ctx.moveTo(chart.chartArea.right, chart.chartArea.top);
        ctx.lineTo(chart.chartArea.right, chart.chartArea.bottom);
        ctx.stroke();
        ctx.restore();
    },
};

const Bar = memo(({ data, labels, legends, optionsProps = {}, parentId }) => {
    const chartRef = useRef(null);

    useChartPrint(chartRef, parentId);

    const options = useMemo(() => createChartOptions(legends, labels, optionsProps), [labels, legends, optionsProps]);
    const gradientData = useMemo(() => withGradientBars(data, optionsProps), [data, optionsProps]);

    return <BarChartJS ref={chartRef} data={gradientData} options={options} plugins={[chartAreaBorder]} />;
});

Bar.displayName = 'Bar';

export default Bar;
